import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import type { User } from '@supabase/supabase-js'
import { createClient } from '@/lib/supabase/client'
import { publicEnv } from '@/config/env/public'
import {
  checkPlatformAdmin,
  getMeusWorkspaces,
  getPerfil,
  getWorkspaceComoAdmin,
} from '@/services/identity'
import type { MeuWorkspace, PapelMembro, PerfilUsuario } from '@/lib/types'

const CURRENT_WS_KEY = 'm360_current_workspace'
const ADMIN_WS_KEY = 'm360_admin_workspace'

export const PUBLIC_PATHS = ['/login', '/onboarding', '/convite', '/p/']
export const ADMIN_PATH_PREFIX = '/admin'

interface AuthContextValue {
  user: User | null
  perfil: PerfilUsuario | null
  workspaces: MeuWorkspace[]
  current: MeuWorkspace | null
  papel: PapelMembro | null
  isPlatformAdmin: boolean
  adminWorkspaceMode: boolean
  loading: boolean
  allowSelfSignup: boolean
  switchWorkspace: (workspaceId: string) => void
  enterWorkspaceAsAdmin: (workspaceId: string) => Promise<void>
  exitAdminWorkspaceMode: () => void
  refresh: () => Promise<void>
  signOut: () => Promise<void>
  pode: (permissao: string) => boolean
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  perfil: null,
  workspaces: [],
  current: null,
  papel: null,
  isPlatformAdmin: false,
  adminWorkspaceMode: false,
  loading: true,
  allowSelfSignup: false,
  switchWorkspace: () => {},
  enterWorkspaceAsAdmin: async () => {},
  exitAdminWorkspaceMode: () => {},
  refresh: async () => {},
  signOut: async () => {},
  pode: () => false,
})

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [perfil, setPerfil] = useState<PerfilUsuario | null>(null)
  const [workspaces, setWorkspaces] = useState<MeuWorkspace[]>([])
  const [current, setCurrent] = useState<MeuWorkspace | null>(null)
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false)
  const [adminWorkspaceMode, setAdminWorkspaceMode] = useState(false)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const location = useLocation()
  const pathname = location.pathname

  const isPublic = PUBLIC_PATHS.some(p => pathname.startsWith(p))
  const isAdminRoute = pathname.startsWith(ADMIN_PATH_PREFIX)
  const allowSelfSignup = publicEnv.allowSelfSignup

  const loadUserData = useCallback(async (u: User) => {
    try {
      const [p, wss, platformAdmin] = await Promise.all([
        getPerfil(u.id),
        getMeusWorkspaces(u.id),
        checkPlatformAdmin(),
      ])
      setPerfil(p)
      setWorkspaces(wss)
      setIsPlatformAdmin(platformAdmin)

      const adminWsId = typeof window !== 'undefined' ? localStorage.getItem(ADMIN_WS_KEY) : null
      if (platformAdmin && adminWsId) {
        const adminWs = await getWorkspaceComoAdmin(adminWsId, u.id)
        if (adminWs) {
          setCurrent(adminWs)
          setAdminWorkspaceMode(true)
          localStorage.setItem(CURRENT_WS_KEY, adminWsId)
          return { needsOnboarding: false, isPlatformAdmin: platformAdmin }
        }
        localStorage.removeItem(ADMIN_WS_KEY)
        setAdminWorkspaceMode(false)
      }

      if (wss.length === 0) {
        setCurrent(null)
        setAdminWorkspaceMode(false)
        return { needsOnboarding: platformAdmin ? false : true, isPlatformAdmin: platformAdmin }
      }

      const savedId = typeof window !== 'undefined' ? localStorage.getItem(CURRENT_WS_KEY) : null
      const found = wss.find(w => w.workspace.id === savedId) ?? wss[0]
      setCurrent(found)
      setAdminWorkspaceMode(false)
      localStorage.setItem(CURRENT_WS_KEY, found.workspace.id)
      return { needsOnboarding: false, isPlatformAdmin: platformAdmin }
    } catch {
      setPerfil(null)
      setWorkspaces([])
      setCurrent(null)
      setIsPlatformAdmin(false)
      setAdminWorkspaceMode(false)
      return { needsOnboarding: true, isPlatformAdmin: false }
    }
  }, [])

  useEffect(() => {
    const supabase = createClient()
    let cancelled = false

    async function init() {
      const { data: { user: u } } = await supabase.auth.getUser()
      if (cancelled) return
      setUser(u)
      if (u) {
        const { needsOnboarding, isPlatformAdmin: platformAdmin } = await loadUserData(u)
        if (!cancelled && needsOnboarding && !isPublic && allowSelfSignup) {
          navigate('/onboarding', { replace: true })
        } else if (!cancelled && needsOnboarding && !isPublic && !allowSelfSignup && !platformAdmin) {
          navigate('/login', { replace: true })
        }
      }
      if (!cancelled) setLoading(false)
    }
    init()

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        setUser(null)
        setPerfil(null)
        setWorkspaces([])
        setCurrent(null)
        setIsPlatformAdmin(false)
        setAdminWorkspaceMode(false)
      }
    })
    return () => { cancelled = true; subscription.unsubscribe() }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loading || !user || isPublic) return
    if (
      isPlatformAdmin &&
      workspaces.length === 0 &&
      !adminWorkspaceMode &&
      !isAdminRoute &&
      pathname !== '/login'
    ) {
      navigate('/admin', { replace: true })
    }
  }, [loading, user, isPlatformAdmin, workspaces.length, adminWorkspaceMode, isAdminRoute, isPublic, pathname, navigate])

  const switchWorkspace = useCallback((workspaceId: string) => {
    localStorage.removeItem(ADMIN_WS_KEY)
    setAdminWorkspaceMode(false)
    setWorkspaces(prev => {
      const found = prev.find(w => w.workspace.id === workspaceId)
      if (found) {
        setCurrent(found)
        localStorage.setItem(CURRENT_WS_KEY, workspaceId)
      }
      return prev
    })
  }, [])

  const enterWorkspaceAsAdmin = useCallback(async (workspaceId: string) => {
    if (!user || !isPlatformAdmin) return
    const ws = await getWorkspaceComoAdmin(workspaceId, user.id)
    if (!ws) throw new Error('Workspace não encontrado')
    localStorage.setItem(ADMIN_WS_KEY, workspaceId)
    localStorage.setItem(CURRENT_WS_KEY, workspaceId)
    setCurrent(ws)
    setAdminWorkspaceMode(true)
    navigate('/')
  }, [user, isPlatformAdmin, navigate])

  const exitAdminWorkspaceMode = useCallback(() => {
    localStorage.removeItem(ADMIN_WS_KEY)
    setAdminWorkspaceMode(false)
    if (workspaces.length > 0) {
      setCurrent(workspaces[0])
      localStorage.setItem(CURRENT_WS_KEY, workspaces[0].workspace.id)
    } else {
      setCurrent(null)
      localStorage.removeItem(CURRENT_WS_KEY)
    }
    navigate('/admin')
  }, [workspaces, navigate])

  const refresh = useCallback(async () => {
    if (user) await loadUserData(user)
  }, [user, loadUserData])

  const signOut = useCallback(async () => {
    const supabase = createClient()
    await supabase.auth.signOut()
    localStorage.removeItem(CURRENT_WS_KEY)
    localStorage.removeItem(ADMIN_WS_KEY)
    navigate('/login', { replace: true })
  }, [navigate])

  const pode = useCallback((permissao: string) => {
    if (!current) return false
    if (isPlatformAdmin) return true
    if (current.membro.papel === 'medico_titular') return true
    return current.membro.permissoes?.[permissao] === true
  }, [current, isPlatformAdmin])

  return (
    <AuthContext.Provider
      value={{
        user,
        perfil,
        workspaces,
        current,
        papel: current?.membro.papel ?? null,
        isPlatformAdmin,
        adminWorkspaceMode,
        loading,
        allowSelfSignup,
        switchWorkspace,
        enterWorkspaceAsAdmin,
        exitAdminWorkspaceMode,
        refresh,
        signOut,
        pode,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
